<template>
  <div>
    <DataTableHeaderContainer>
      <div class="d-flex align-center">
        <div>
          <h1>{{ $t('admin.bookLists.title') }}</h1>
          <p class="mb-0 grey--text text--darken-1">
            {{ $t('admin.bookLists.description') }}
          </p>
        </div>
        <v-spacer />
        <v-btn
          color="primary"
          class="mr-2"
          @click="openCreateDialog"
        >
          {{ $t('admin.bookLists.createBookList') }}
        </v-btn>
        <v-btn
          color="primary"
          outlined
          :loading="loading"
          @click="fetchBookLists"
        >
          {{ $t('admin.refreshData') }}
        </v-btn>
      </div>
    </DataTableHeaderContainer>

    <v-dialog
      v-model="createDialog"
      max-width="560px"
    >
      <v-card>
        <v-card-title>
          {{ $t('admin.bookLists.createBookList') }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newBookListName"
            :label="$t('bookList.listName')"
            outlined
            dense
          />
          <v-textarea
            v-model="newBookListNotes"
            :label="$t('bookList.notes')"
            outlined
            dense
          />
          <v-text-field
            v-model="newBookListDiscountPercentage"
            :label="$t('bookList.discountPercentage')"
            type="number"
            outlined
            dense
          />
          <EkitabuSchoolSelect
            v-model="newBookListSchoolId"
            clearable
            :required="false"
          />
          <B2bCustomerSelect
            v-model="newBookListB2bCustomerId"
          />
          <TaxRegionSelect
            v-model="newBookListTaxRateId"
          />
          <v-switch
            v-model="newBookListIsPublic"
            :label="$t('bookList.publicShareable')"
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="createDialog = false"
          >
            {{ $t('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="creating"
            :disabled="creating || !newBookListName.trim()"
            @click="createBookList"
          >
            {{ $t('button.apply') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container class="admin-booklists-container mt-6 pa-3">
      <v-text-field
        v-model="search"
        :label="$t('admin.bookLists.searchPlaceholder')"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        clearable
      />

      <ekitabu-data-table
        :headers="headers"
        :items="bookLists"
        :loading="loading"
        :search="search"
        :items-per-page="25"
        class="elevation-0"
      >
        <template #item.name="{ item }">
          <router-link :to="bookListRoute(item)">
            {{ item.name }}
          </router-link>
        </template>

        <template #item.audience="{ item }">
          {{ audienceLabel(item) }}
        </template>

        <template #item.visibility="{ item }">
          <v-chip
            small
            :color="item.isPublic ? 'success' : 'grey'"
            text-color="white"
          >
            {{ item.isPublic ? $t('admin.bookLists.public') : $t('admin.bookLists.private') }}
          </v-chip>
        </template>

        <template #item.itemCount="{ item }">
          {{ item.itemCount ?? 0 }}
        </template>

        <template #item.updatedAt="{ item }">
          {{ formatDate(item.updatedAt) }}
        </template>

        <template #item.actions="{ item }">
          <v-btn
            small
            color="primary"
            text
            :to="bookListRoute(item)"
          >
            {{ $t('admin.viewDetails') }}
          </v-btn>
        </template>

        <template #no-data>
          {{ $t('admin.bookLists.noBookLists') }}
        </template>
      </ekitabu-data-table>
    </v-container>
  </div>
</template>

<script>
import DataTableHeaderContainer from '@/components/tables/DataTableHeaderContainer.vue';
import BookListQueries from '@/graphql/BookListQueries';
import EkitabuSchoolSelect from '@/components/inputs/EkitabuSchoolSelect.vue';
import B2bCustomerSelect from '@/components/inputs/B2bCustomerSelect.vue';
import TaxRegionSelect from '@/components/inputs/TaxRegionSelect.vue';

export default {
  name: "AdminBookLists",
  components: {
    DataTableHeaderContainer,
    EkitabuSchoolSelect,
    B2bCustomerSelect,
    TaxRegionSelect,
  },
  metaInfo() {
    return {
      title: this.$t('routes.adminBookLists'),
    };
  },
  data() {
    return {
      bookLists: [],
      loading: false,
      search: "",
      createDialog: false,
      creating: false,
      newBookListName: "",
      newBookListNotes: "",
      newBookListDiscountPercentage: null,
      newBookListSchoolId: null,
      newBookListB2bCustomerId: null,
      newBookListTaxRateId: null,
      newBookListIsPublic: false,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('admin.bookLists.name'), value: 'name' },
        { text: this.$t('admin.bookLists.audience'), value: 'audience' },
        { text: this.$t('admin.bookLists.visibility'), value: 'visibility' },
        { text: this.$t('admin.bookLists.items'), value: 'itemCount' },
        { text: this.$t('admin.bookLists.owner'), value: 'customerId' },
        { text: this.$t('admin.bookLists.updated'), value: 'updatedAt' },
        { text: '', value: 'actions', sortable: false },
      ];
    },
  },
  created() {
    this.fetchBookLists();
  },
  methods: {
    openCreateDialog() {
      this.newBookListName = "";
      this.newBookListNotes = "";
      this.newBookListDiscountPercentage = null;
      this.newBookListSchoolId = null;
      this.newBookListB2bCustomerId = null;
      this.newBookListTaxRateId = null;
      this.newBookListIsPublic = false;
      this.createDialog = true;
    },
    // The backend resolves forSchool/forB2bCustomer via getResourceFromIri(),
    // so a bare id must be turned into an IRI. Guard against double-prefixing in
    // case a selector ever starts emitting a full IRI.
    toIri(value, prefix) {
      if (!value) return null;
      return String(value).startsWith('/') ? value : `${prefix}/${value}`;
    },
    async createBookList() {
      if (!this.newBookListName.trim()) return;

      this.creating = true;
      try {
        await this.$apollo.mutate({
          mutation: BookListQueries.createBookList,
          variables: {
            input: {
              name: this.newBookListName.trim(),
              notes: this.newBookListNotes || null,
              discountPercentage: this.newBookListDiscountPercentage != null && this.newBookListDiscountPercentage !== ''
                ? parseFloat(this.newBookListDiscountPercentage)
                : null,
              forSchool: this.toIri(this.newBookListSchoolId, '/schools'),
              forB2bCustomer: this.toIri(this.newBookListB2bCustomerId, '/b2b_customers'),
              // TaxRegionSelect binds item-value="id", so this is already an IRI.
              taxRate: this.newBookListTaxRateId || null,
              isPublic: this.newBookListIsPublic,
              books: [],
            },
          },
        });
        this.createDialog = false;
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('admin.bookLists.createdSuccessfully'),
          color: 'success',
        });
        await this.fetchBookLists();
      } catch (error) {
        console.error("Error creating admin booklist:", error);
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('errors.general'),
          color: 'error',
        });
      } finally {
        this.creating = false;
      }
    },
    async fetchBookLists() {
      this.loading = true;
      try {
        const result = await this.$apollo.query({
          query: BookListQueries.getAdminBookLists,
          fetchPolicy: 'network-only',
        });
        this.bookLists = this.withItemCounts(result.data.bookLists || []);
      } catch (error) {
        if (this.isAdminBookListSchemaMismatch(error)) {
          await this.fetchLegacyBookLists();
        } else {
          console.error("Error fetching admin booklists:", error);
          this.$store.dispatch('snackbar/showSnackbar', {
            text: this.$t('errors.general'),
            color: 'error',
          });
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchLegacyBookLists() {
      const result = await this.$apollo.query({
        query: BookListQueries.getAdminBookListsLegacy,
        fetchPolicy: 'network-only',
      });
      this.bookLists = this.withItemCounts(result.data.bookLists || []);
    },
    isAdminBookListSchemaMismatch(error) {
      const messages = [
        ...(error.graphQLErrors || []).map((graphQLError) => graphQLError.message),
        error.message,
      ].filter(Boolean).join(' ');
      return messages.includes('forB2bCustomer') || messages.includes('itemCount');
    },
    withItemCounts(bookLists) {
      return bookLists.map((bookList) => ({
        ...bookList,
        itemCount: bookList.itemCount ?? bookList.bookListItems?.length ?? 0,
      }));
    },
    bookListRoute(item) {
      return {
        name: 'userBookList',
        params: { id: item._id },
      };
    },
    audienceLabel(item) {
      if (item.forSchool && item.forB2bCustomer) {
        return `${item.forSchool.name} — ${item.forB2bCustomer.organizationName}`;
      }
      if (item.forB2bCustomer) {
        return item.forB2bCustomer.organizationName;
      }
      if (item.forSchool) {
        return item.forSchool.name;
      }
      if (item.customerId) {
        return this.$t('admin.bookLists.personal');
      }
      return this.$t('admin.bookLists.shared');
    },
    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.admin-booklists-container {
  background-color: var(--v-lightestAccent-base);
}
</style>
